import axios from 'axios';
import { useAuthorStore } from '@/stores';
import { Emitter } from '@/utils';
import { ApiEventNames } from '@/constants/common';

/**
 * 获取令牌
 * @returns {Promise<string>}
 */
const getToken = async () => {
    const store = useAuthorStore();
    const { token } = store;
    return token;
};

/**
 * 设置令牌
 * @param token
 * @returns {Promise<void>}
 */
const setToken = async (token) => {
    const store = useAuthorStore();
    store.setToken(token);
    return Promise.resolve();
};

const makeQueryParamsStr = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    if (typeof obj !== 'object') return '';
    const keys = Object.keys(obj);
    keys.sort((a, b) => {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    });
    const result = [];
    for (const k of keys) {
        const v = obj[k] || '';
        if (!v) continue;
        result.push(`${k}=${v}`);
    }
    return result.join('&');
};

const parseUrlParams = (url) => {
    const regEx = /[?&][^?&]+=[^?&]+/g;
    const arr = url.match(regEx);
    const obj = {};
    if (arr) {
        arr.forEach((item) => {
            const tempArr = item.substring(1).split('=');
            const key = decodeURIComponent(tempArr[0]);
            obj[key] = decodeURIComponent(tempArr[1]);
        });
    }
    return obj;
};

const mergeUrlQueryParams = (url, params) => {
    if (!url) {
        return url;
    }
    const urlParams = parseUrlParams(url) || {};
    const queryParams = { ...urlParams, ...params };
    const queryStr = makeQueryParamsStr(queryParams);
    let newUrl = url.split('?')[0];
    if (queryStr) newUrl += '?' + queryStr;
    return newUrl;
};

// 创建axios实例
const service = axios.create({
    baseURL: '/', // process.env.VUE_APP_BASE_API, // URL地址   环境变量文件 .env.development
    timeout: 30 * 60 * 1000, // 超时
    withCredentials: true, // 跨域时若要发生cookie,需要设置该选项
});

// service.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
// 请求拦截器
service.interceptors.request.use(
    async config => {
        const token = await getToken();
        if (token) {
            // 设置令牌请求头
            config.headers['x-access-token'] = `bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.resolve([ null, error.message || '网络错误', 400 ]);
    },
);

// new Emitter, 返回topic主题单例
const eventBus = new Emitter({}, 'api-event-bus');

// 相应拦截
service.interceptors.response.use(
    // 通过自定义code 判定响应状态 也可以通过HTTP状态码判断
    async response => {
        const { fetchOptions = {} } = response.config;
        const { showError = true } = fetchOptions || {};
        const { code, msg, content } = response.data;
        // code 不为0 则判断为一个错误
        if (code !== 0) {
            if (code === 401) {
                eventBus.emit(ApiEventNames.Forbidden, { error: '无权操作', showError });
            } else if (code === 403) {
                eventBus.emit(ApiEventNames.Unauthorized, { error: '未登录或登录超时', showError });
            } else {
                eventBus.emit(ApiEventNames.ApiError, { error: msg, showError });
            }
            return Promise.resolve([ null, msg, 200 ]);
        }
        // 更新令牌
        const newToken = !!response && !!response.headers
            ? response.headers['new-token'] || response.headers.get('New-Token')
            : '';
        if (newToken) {
            await setToken(newToken).catch(e => console.error(e));
        }
        return Promise.resolve([ content, '', 200 ]);
    },
    error => {
        if (!error.response) {
            return Promise.resolve([ null, error.message || '网络错误', 400 ]);
        }
        const { fetchOptions = {} } = error.response.config;
        const { showError = true } = fetchOptions || {};
        if (error.response.status === 401) {
            // 未签权或令牌无效或过期
            eventBus.emit(ApiEventNames.Forbidden, { error: '无权操作', showError });
            return Promise.resolve([ null, 'unauthorized', 401 ]);
        } else if (error.response.status === 403) {
            // 未登录或登录超时
            eventBus.emit(ApiEventNames.Unauthorized, { error: '未登录或登录超时', showError });
            return Promise.resolve([ null, 'forbidden', 403 ]);
        }
        // 其他错误
        eventBus.emit(ApiEventNames.ApiError, { error: error.message, showError });
        return Promise.resolve([ null, error.response.data.msg || error.message, error.response.status ]);
    },
);

/**
 * POST 请求
 * @param url
 * @param data
 * @param options {{ showError: boolean }}
 * @param config
 * @returns {Promise<Awaited<*[]>|Awaited<axios.AxiosResponse<any>[]>>}
 * @constructor
 */
export const POST = async (url, data, options = { showError: true }, config = null) => {
    try {
        const { accessToken, ...formData } = data || {};
        if (accessToken) {
            config = {
                ...config,
                headers: { 'x-access-token': `bearer ${accessToken}` },
            };
        }
        return await service.post(url, formData, {
            ...config,
            fetchOptions: { showError: options.showError || false },
        });
    } catch (err) {
        return Promise.resolve([ null, err.message || err, 400 ]);
    }
};

/**
 * GET 请求
 * @param url
 * @param data
 * @param options {{ showError: boolean }}
 * @param config
 * @returns {Promise<Awaited<*[]>|Awaited<axios.AxiosResponse<any>[]>>}
 * @constructor
 */
export const GET = async (url, data, options = { showError: true }, config = null) => {
    try {
        const { accessToken, ...formData } = data || {};
        if (accessToken) {
            config = { ...config, headers: { 'x-access-token': `bearer ${accessToken}` } };
        }
        const queryUrl = mergeUrlQueryParams(url, formData);
        return await service.get(queryUrl, {
            ...config,
            fetchOptions: { showError: options.showError },
        });
    } catch (err) {
        return Promise.resolve([ null, err.message || err, 400 ]);
    }
};

/**
 * PUT 请求
 * @param url
 * @param data
 * @param options {{ showError: boolean }}
 * @param config
 * @returns {Promise<Awaited<*[]>|Awaited<axios.AxiosResponse<any>[]>>}
 * @constructor
 */
export const PUT = async (url, data, options = { showError: true }, config = null) => {
    try {
        return await service.put(url, data, {
            ...config,
            fetchOptions: { showError: options.showError },
        });
    } catch (err) {
        return Promise.resolve([ null, err.message || err, 400 ]);
    }
};

/**
 * DELETE 请求
 * @param url
 * @param options {{ showError: boolean }}
 * @param config
 * @returns {Promise<Awaited<*[]>|Awaited<axios.AxiosResponse<any>[]>>}
 * @constructor
 */
export const DELETE = async (url, options = { showError: true }, config = null) => {
    try {
        return await service.delete(url, { ...config, fetchOptions: { showError: options.showError } });
    } catch (err) {
        return Promise.resolve([ null, err.message || err, 400 ]);
    }
};

/**
 * PATCH 请求
 * @param url
 * @param options {{ showError: boolean }}
 * @param config
 * @returns {Promise<Awaited<*[]>|Awaited<axios.AxiosResponse<any>[]>>}
 */
export const OPTIONS = async (url, options = { showError: true }, config = null) => {
    try {
        return await service.options(url, { ...config, fetchOptions: { showError: options.showError } });
    } catch (err) {
        return Promise.resolve([ null, err.message || err, 400 ]);
    }
};

/**
 * HEAD 请求
 * @param url
 * @param options {{ showError: boolean }}
 * @param config
 * @returns {Promise<Awaited<*[]>|Awaited<axios.AxiosResponse<any>[]>>}
 */
export const HEAD = async (url, options = { showError: true }, config = null) => {
    try {
        return await service.head(url, { ...config, fetchOptions: { showError: options.showError } });
    } catch (err) {
        return Promise.resolve([ null, err.message || err, 400 ]);
    }
};

/**
 * 提交数据及上传文件（高阶函数）
 * @param url 提交地址
 * @param files
 * @param data
 * @param headers
 * @param options {{ onUploadProgress: (function(event):void)|undefined, showError: boolean }}
 * @returns {Promise<Awaited<*>|Awaited<axios.AxiosResponse<any>>>}
 * @constructor
 */
export const UPLOAD = async (
    url,
    data = {},
    files = [],
    headers = {},
    options = {
        onUploadProgress: undefined,
        showError: true,
    },
) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
        formData.append(files[i].key || 'file', files[i].file, files[i].name || `file_${i}`);
    }

    Object.entries(data).forEach(([ k, v ]) => {
        formData.append(k, v);
    });

    const config = {
        // method: 'post',
        // url: url,
        // data: formData,
        // headers: {},
        withCredentials: true,
        fetchOptions: { showError: options.showError },
        'onUploadProgress' (event) {
            const { onUploadProgress } = options || {};
            !!onUploadProgress && onUploadProgress(event);
        },
    };
    try {
        return service.post(url, formData, config);
    } catch (err) {
        return Promise.resolve([ null, err.message || err, 400 ]);
    }
    // const token = await getToken();
    // if (token) {
    //     // 设置令牌请求头
    //     config.headers['x-access-token'] = token;
    // }
    // Object.assign(config.headers, headers);
    //
    // const rs = await axios(url, config);
    // if (rs.status === 200) {
    //     // 服务器返回正确
    //     if (rs.data.code) {
    //         return Promise.resolve([ null, rs.data.msg, 200 ]); // 出错
    //     }
    //     return Promise.resolve([ rs.data.content, null, 200 ]);
    // }
    // if (rs.status === 400) {
    //     // 参数错误
    //     return Promise.resolve([ null, '参数错误', 400 ]);
    // } else if (rs.status === 401) {
    //     // 未签权
    //     return Promise.resolve([ null, 'unauthorized', 401 ]);
    // } else if (rs.status === 403) {
    //     // 令牌无效或过期或无权
    //     return Promise.resolve([ null, 'forbidden', 403 ]);
    // } else if (rs.status === 404) {
    //     // 地址无效
    //     return Promise.resolve([ null, '请求地址无效', 404 ]);
    // } else if (rs.status === 500) {
    //     // 服务器内部错误
    //     return Promise.resolve([ null, rs.statusText, 500 ]);
    // }
};

export default service;
